import React, { forwardRef, Fragment, memo, } from 'react';
import BulletList, { BulletListVariants, } from '@polestar/component-warehouse-react/components/bullet-list/BulletList';
import Image from '@polestar/component-warehouse-react/components/image/Image';
import Label from '@polestar/component-warehouse-react/components/label/Label';
import Link from '@polestar/component-warehouse-react/components/link/Link';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import Picture from '@polestar/component-warehouse-react/components/picture/Picture';
import Quote from '@polestar/component-warehouse-react/components/quote/Quote';
import Section from '@polestar/component-warehouse-react/components/section/Section';
import Table, { TableBody, TableCell, TableHead, TableRow, TableHeader, } from '@polestar/component-warehouse-react/components/table/Table';
import TextareaField from '@polestar/component-warehouse-react/components/textarea-field/TextareaField';
import VideoPlayer from '@polestar/component-warehouse-react/components/video-player/VideoPlayer';
import Caption from '@polestar/component-warehouse-react/components/caption/Caption';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Text from '@polestar/component-warehouse-react/components/text/Text';
import { H1, H2, H3, H4, H5, H6, } from '@polestar/component-warehouse-react/components/heading/Heading';
import parse, { attributesToProps, domToReact, } from 'html-react-parser';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
const elementWithoutSpacing = [
    'default',
    'sub',
    'sup',
    'a',
    'br',
    'hr',
    'strong',
    'b',
    'span',
    'td',
    'tr',
    'th',
    'thead',
    'tbody',
    'source',
];
// List of how to map elements to components
const elementMap = {
    // Without spacing
    default: Fragment,
    sub: 'sub',
    sup: 'sup',
    a: Link,
    br: [Spacer, { as: 'span' }],
    div: 'div',
    span: 'span',
    hr: 'hr',
    strong: [Text, { as: 'strong' }],
    b: [Text, { as: 'strong' }],
    td: TableCell,
    tr: TableRow,
    th: TableHeader,
    thead: TableHead,
    tbody: TableBody,
    source: 'source',
    // With spacing
    h1: [H1, { as: 'h1' }],
    h2: [H2, { as: 'h2' }],
    h3: [H3, { as: 'h3' }],
    h4: [H4, { as: 'h4' }],
    h5: [H5, { as: 'h5' }],
    h6: [H6, { as: 'h6' }],
    label: Label,
    blockquote: Quote,
    p: Paragraph,
    ul: [BulletList, { variant: BulletListVariants.bullets }],
    ol: [BulletList, { variant: BulletListVariants.numbered }],
    li: Text,
    table: Table,
    textarea: TextareaField,
    figure: 'figure',
    img: Image,
    picture: Picture,
    video: VideoPlayer,
    section: Section,
    caption: Caption,
    figcaption: Caption,
};
const defaultElementMap = {};
// Function to create a dynamic component based on the provided parameters
const createDynamicComponent = ({ key, hasSpacing, Component, propsOverwrite, }) => {
    const ElementComponent = ({ isFirst, ...props }) => {
        if (key === 'img' && (props === null || props === void 0 ? void 0 : props.alt) === undefined) {
            // eslint-disable-next-line no-console
            console.warn('ConvertHTMLtoComponents: provide an alt text to an image element to support accessibility.');
        }
        if (key === 'li') {
            // spacing is done already in the UL/OL so do not add extra unwanted spacing
            hasSpacing = false;
        }
        return (React.createElement(Fragment, null,
            hasSpacing && !isFirst && React.createElement(Spacer, null),
            React.createElement(Component, { ...props, ...propsOverwrite })));
    };
    ElementComponent.displayName = (Component === null || Component === void 0 ? void 0 : Component.displayName) || `ConvertHTML(${key})`;
    return ElementComponent;
};
/**
 * Define a lookup table corresponding with the correct components
 * Only run once to define the defaultElementMap
 */
for (const key in elementMap) {
    let Component = elementMap[key];
    let propsOverwrite = {};
    const hasSpacing = !elementWithoutSpacing.includes(key);
    if (Array.isArray(Component)) {
        propsOverwrite = Component[1];
        Component = Component[0];
    }
    defaultElementMap[key] = createDynamicComponent({
        key,
        hasSpacing,
        Component,
        propsOverwrite,
    });
}
const DOMElementType = {
    script: 'script',
    tag: 'tag',
    style: 'style',
    text: 'text',
    comment: 'comment',
};
// TODO: script tags are removed as previous implementation provided that as a function, if needed this can be removed but XSS will be more important
const parseChildren = (children, options) => {
    return children
        .filter((child) => {
        var _a, _b, _c, _d;
        return (
        // skip invalid script tags that are not closed properly
        (child.type === 'script' && child.children.length === 0) ||
            // empty text
            (child.type === 'text' && ((_a = child === null || child === void 0 ? void 0 : child.data) === null || _a === void 0 ? void 0 : _a.trim()) === '') ||
            // a child without any text
            (child.type !== 'tag' &&
                ((_b = child.children) === null || _b === void 0 ? void 0 : _b.length) === 1 &&
                ((_d = (_c = child.children[0]) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.trim()) === '')
            ? false
            : true);
    })
        .map((child) => {
        var _a, _b, _c, _d, _e;
        if (
        // a tag with a child without any text
        ((_a = child.children) === null || _a === void 0 ? void 0 : _a.length) === 1 &&
            ((_c = (_b = child.children[0]) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.trim()) === '') {
            return ' ';
        }
        if (child.type === 'script') {
            // check when the content has a HTMLElement inside then it is not enclosed properly, otherwise it is just code that is defined inside the script tag.
            const hasHTMLElements = ((_e = (_d = child === null || child === void 0 ? void 0 : child.children) === null || _d === void 0 ? void 0 : _d.filter((child) => {
                var _a, _b;
                // for normal HTML tags
                return ((_a = child === null || child === void 0 ? void 0 : child.data) === null || _a === void 0 ? void 0 : _a.includes('</')) ||
                    (
                    // for self enclosed tags
                    (_b = child === null || child === void 0 ? void 0 : child.data) === null || _b === void 0 ? void 0 : _b.includes('/>'));
            })) === null || _e === void 0 ? void 0 : _e.length) > 0 || false;
            if (hasHTMLElements) {
                // eslint-disable-next-line no-console
                console.warn('A script tag is not closed properly and therefore invalid HTML with the following properties', child === null || child === void 0 ? void 0 : child.attribs);
            }
            // has some children while it is not enclosed properly
            return child.children.map((val) => {
                const items = parse(val.data, options);
                if (Array.isArray(items)) {
                    // the parse function provides ReactNodes but ignore the empty ones
                    return items.filter((item) => typeof item === 'string' && (item === null || item === void 0 ? void 0 : item.trim()) === '' ? false : true);
                }
                return null;
            });
        }
        return domToReact([child], options);
    });
};
const parseDOMElements = (domNode, options, map, memoizedId, index) => {
    switch (domNode.type) {
        // valid (closed) script tags (without any children) will be captured here
        case DOMElementType.script:
            // ignore script tags
            return React.createElement(React.Fragment, null);
        case DOMElementType.text:
            return domNode.data;
        case DOMElementType.tag:
            if (!domNode.name) {
                // an invalid tag without a name, so ignore
                return React.createElement(React.Fragment, null);
            }
            const name = domNode.name;
            // @ts-ignore (property is not typed, but available in the response)
            const children = domNode.children;
            const prev = domNode.prev;
            const isFirst = prev === null ||
                // there is a newline added as prev for all items within a parent, ignore it to find out if its the first item
                (prev.type === 'text' && prev.data.includes('\n') && prev.prev === null);
            const Component = (map[name] ||
                map.default);
            let content;
            if (children.length > 0) {
                content = parseChildren(children, options);
            }
            if ((content === null || content === void 0 ? void 0 : content.length) === 0) {
                return ' ';
            }
            // Parse attributes such as style, id and class
            const props = attributesToProps(domNode.attribs);
            return (React.createElement(Component, { key: `${memoizedId}-${name}-${index}`, isFirst: isFirst, ...props }, content));
    }
    return React.createElement(React.Fragment, null);
};
const ConvertHTMLtoComponents = forwardRef(({ children, overrideElementMap = {}, ...props }, ref) => {
    const childCount = React.Children.count(children);
    // generate a unique key for each element, since the index is not unique
    const memoizedId = useSSRSafeId(`convert-html-to-components`);
    if (childCount !== 1 || typeof children !== 'string') {
        // eslint-disable-next-line no-console
        console.warn('ConvertHTMLtoComponents: provide a string so it can be transformed correctly.');
    }
    else {
        const map = { ...defaultElementMap, ...overrideElementMap };
        let index = 0;
        const options = {
            replace(domNode) {
                index++;
                return parseDOMElements(domNode, options, map, memoizedId, index);
            },
        };
        const parsed = parse(children, options);
        return (React.createElement("div", { ref: ref, ...props }, parsed));
    }
    return React.createElement("div", { ref: ref, ...props });
});
export default memo(ConvertHTMLtoComponents);
